/**
 * A leading thumbnail for a picker row.
 *
 * Three sources of a frame, in order of what it costs:
 *
 *   1. A stored `thumbnailUrl`. Drills and pro videos have the field, set by
 *      hand in the admin library. Free — it is an <img>.
 *   2. A frame generated from the video. Costs one hidden <video> that opens
 *      the file, seeks, and paints one frame to a canvas.
 *   3. Nothing renderable. The slot keeps its placeholder.
 *
 * Three things keep (2) from being ruinous on a long list:
 *
 *   - A session cache, module-level so it survives the picker closing and
 *     reopening. Scrolling back never regenerates.
 *   - A concurrency cap. Twenty rows do not open twenty decoders.
 *   - An IntersectionObserver. A row that is never scrolled to never
 *     generates, which is what bounds the work on an unpaginated list.
 *
 * The slot is a fixed box either way, so nothing moves when a frame arrives.
 */

import { useEffect, useRef, useState } from 'react';

/** 16:9, matching the composite's per-source box. */
const THUMB_W = 160;
const THUMB_H = 90;

/**
 * Three at a time.
 *
 * Each generation opens a decoder against a Firebase Storage URL, so the cost
 * is a network fetch plus a seek, not just CPU. Three keeps a scroll feeling
 * responsive without queueing behind a long tail; one is visibly slow on a
 * list of twenty and eight saturates a phone.
 */
const MAX_CONCURRENT = 3;

/** cacheKey → data URL. Module-level: survives unmount, cleared on reload. */
const frameCache = new Map<string, string>();

let running = 0;
const queue: (() => void)[] = [];

function runNext() {
  if (running >= MAX_CONCURRENT) return;
  const job = queue.shift();
  if (!job) return;
  running += 1;
  job();
}

function schedule<T>(job: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    queue.push(() => {
      job()
        .then(resolve, reject)
        .finally(() => {
          running -= 1;
          runNext();
        });
    });
    runNext();
  });
}

/** Opens the file, seeks, paints one frame. Resolves null on any failure. */
function captureFrame(url: string, time: number): Promise<string | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    let settled = false;
    const done = (out: string | null) => {
      if (settled) return;
      settled = true;
      video.src = '';
      video.load();
      resolve(out);
    };
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    // A stuck seek would otherwise hold a slot in the queue forever.
    const timer = setTimeout(() => done(null), 8000);
    video.onloadeddata = () => {
      try {
        video.currentTime = Math.max(0, Math.min(time, (video.duration || 0) - 0.05));
      } catch {
        clearTimeout(timer);
        done(null);
      }
    };
    video.onseeked = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = THUMB_W;
        canvas.height = THUMB_H;
        const ctx = canvas.getContext('2d');
        if (!ctx) return done(null);
        // Contained, not cropped — the same relationship the composite draws.
        const vw = video.videoWidth || THUMB_W;
        const vh = video.videoHeight || THUMB_H;
        const scale = Math.min(THUMB_W / vw, THUMB_H / vh);
        const w = vw * scale;
        const h = vh * scale;
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, THUMB_W, THUMB_H);
        ctx.drawImage(video, (THUMB_W - w) / 2, (THUMB_H - h) / 2, w, h);
        done(canvas.toDataURL('image/jpeg', 0.7));
      } catch {
        // Tainted canvas (a URL without CORS headers) lands here.
        done(null);
      }
    };
    video.onerror = () => { clearTimeout(timer); done(null); };
    video.src = url;
  });
}

export function RowThumbnail({
  cacheKey,
  thumbnailUrl,
  videoUrl,
  resolveVideoUrl,
  time = 0,
  isCameraSource = false,
}: {
  /** Stable per row, so a regenerated list reuses the cached frame. */
  cacheKey: string;
  /** A stored thumbnail, used as-is when present. */
  thumbnailUrl?: string;
  /** Generate from this when there is no stored thumbnail. */
  videoUrl?: string;
  /** For sources whose URL has to be fetched first, e.g. the player library. */
  resolveVideoUrl?: () => Promise<string | null>;
  time?: number;
  isCameraSource?: boolean;
}) {
  const [frame, setFrame] = useState<string | null>(() => frameCache.get(cacheKey) ?? null);
  const slotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (thumbnailUrl || isCameraSource) return;
    const cached = frameCache.get(cacheKey);
    if (cached) { setFrame(cached); return; }
    const el = slotRef.current;
    if (!el) return;

    let cancelled = false;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer.disconnect();
      schedule(async () => {
        const url = videoUrl ?? (resolveVideoUrl ? await resolveVideoUrl() : null);
        if (!url || cancelled) return null;
        const out = await captureFrame(url, time);
        if (out && !cancelled) {
          frameCache.set(cacheKey, out);
          setFrame(out);
        }
        return out;
      }).catch(() => {});
    });
    observer.observe(el);
    return () => { cancelled = true; observer.disconnect(); };
  // resolveVideoUrl is a fresh closure per render; the cacheKey is what identifies the work.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, thumbnailUrl, videoUrl, time, isCameraSource]);

  const src = thumbnailUrl ?? frame;

  return (
    <div
      ref={slotRef}
      /* Fixed box, always. The placeholder and the loaded frame occupy exactly
         the same space, so a row never reflows when an image arrives. */
      className="w-16 h-9 flex-shrink-0 rounded overflow-hidden flex items-center justify-center"
      style={{ background: 'var(--editor-timeline)' }}
      aria-hidden="true"
    >
      {src ? (
        <img src={src} alt="" className="w-full h-full object-cover" />
      ) : (
        <svg className="w-4 h-4 text-[var(--editor-text-muted)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          {isCameraSource ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 6.75h.008v.008H18V6.75z" />
          )}
        </svg>
      )}
    </div>
  );
}
